import type { PopoverContentProps, ProviderProps } from '@reactour/tour'
import { TourProvider } from '@reactour/tour'
import Group from 'components/utils/Group'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Caption from 'LuxKit/Typography/Caption'
import { rem } from 'polished'
import { useCallback, useMemo, type ComponentProps, type ReactNode } from 'react'
import styled, { useTheme } from 'styled-components'
import zIndex from 'styles/tools/z-index'
import useAgentHubBookingOnboarding from './useAgentHubBookingOnboarding'
import useAgentHubBookingOnboardingTourSteps from './useAgentHubBookingOnboardingTourSteps'

const TOOLTIP_WIDTH = 320

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${rem(8)};
  inset-inline-end: ${rem(8)};
`

const StepBadge = styled(Caption)`
  display: block;
  margin-bottom: ${rem(12)};
`

const TourNavigation = styled(Group)`
  margin-top: ${rem(16)};
`

type TourBadgeProps = ComponentProps<
  NonNullable<NonNullable<ProviderProps['components']>['Badge']>
> & {
  children?: ReactNode
}

function AgentHubOnboardingBadge({ children }: TourBadgeProps) {
  return (
    <StepBadge variant="medium" colour="neutral-two">
      {children}
    </StepBadge>
  )
}

function getTourStyles(borderRadius: string): NonNullable<ProviderProps['styles']> {
  return {
    maskWrapper: (base) => ({
      ...base,
      color: 'color-mix(in srgb, var(--palette-neutral-surface-normal-foreground) 60%, transparent)',
      opacity: 1,
      zIndex: zIndex.modal + 2,
    }),
    popover: (base) => ({
      ...base,
      backgroundColor: 'var(--palette-neutral-surface-normal-background)',
      borderRadius,
      boxShadow: 'var(--shadow-bottom-medium)',
      maxWidth: rem(TOOLTIP_WIDTH),
      padding: rem(16),
      width: rem(TOOLTIP_WIDTH),
      zIndex: zIndex.modal + 3,
    }),
  }
}

function AgentHubBookingOnboardingWalkthrough() {
  const theme = useTheme()
  const { complete, dismiss } = useAgentHubBookingOnboarding()
  const tourStyles = useMemo(() => getTourStyles(theme.borderRadius.M), [theme.borderRadius.M])
  const {
    backLabel,
    badgeContent,
    closeLabel,
    doneLabel,
    nextLabel,
    skipLabel,
    steps,
  } = useAgentHubBookingOnboardingTourSteps()

  const onDismiss = useCallback(() => {
    dismiss()
  }, [dismiss])

  const onBack = useCallback((setCurrentStep: PopoverContentProps['setCurrentStep']) => {
    setCurrentStep(current => Math.max(0, current - 1))
  }, [])

  const onNext = useCallback((
    currentStep: number,
    stepCount: number,
    setCurrentStep: PopoverContentProps['setCurrentStep'],
  ) => {
    if (currentStep === stepCount - 1) {
      complete()
      return
    }

    setCurrentStep(current => current + 1)
  }, [complete])

  const tourComponents = useMemo<NonNullable<ProviderProps['components']>>(() => ({
    Badge: AgentHubOnboardingBadge,
    Close: ({ onClick }) => (
      <CloseButton kind="tertiary" size="small" onClick={onClick} aria-label={closeLabel}>
        <LineTimesIcon />
      </CloseButton>
    ),
    Navigation: ({ currentStep, setCurrentStep, steps: tourSteps }: PopoverContentProps) => {
      const isLastStep = currentStep === tourSteps.length - 1

      return (
        <TourNavigation direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
          <TextButton kind="tertiary" onClick={onDismiss}>
            {skipLabel}
          </TextButton>
          <Group direction="horizontal" gap={8}>
            <TextButton kind="tertiary" onClick={() => onBack(setCurrentStep)} disabled={currentStep === 0}>
              {backLabel}
            </TextButton>
            <TextButton kind="primary" onClick={() => onNext(currentStep, tourSteps.length, setCurrentStep)}>
              {isLastStep ? doneLabel : nextLabel}
            </TextButton>
          </Group>
        </TourNavigation>
      )
    },
  }), [backLabel, closeLabel, doneLabel, nextLabel, onBack, onDismiss, onNext, skipLabel])

  return (
    <TourProvider
      steps={steps}
      styles={tourStyles}
      components={tourComponents}
      badgeContent={badgeContent}
      defaultOpen
      showDots={false}
      disableInteraction={false}
      onClickClose={onDismiss}
      onClickMask={onDismiss}
      accessibilityOptions={{
        ariaLabelledBy: 'agent-hub-booking-onboarding-title',
        closeButtonAriaLabel: closeLabel,
        showNavigationScreenReaders: true,
      }}
    >
      {null}
    </TourProvider>
  )
}

export default AgentHubBookingOnboardingWalkthrough
